import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useRef } from 'react';
import { useSubscription } from '@meraki-internal/state';
import { WeatherConditionIcon } from '../components/WeatherConditionIcon';
import { HourlyWeatherChartPageViewModel } from './HourlyWeatherChartPageViewModel';
import { IWeatherCondition } from '../model/IWeatherCondition';
import { ChartJSProvider } from './ChartJSProvider';

export class ChartTooltipConfig {
    backgroundColor = '--ion-color-light';
}

export const ChartTooltip: React.FC = () => {
    const vm = useInstance(HourlyWeatherChartPageViewModel);
    useSubscription(() => vm);

    const chartJSProvider = useInstance(ChartJSProvider);

    if (!chartJSProvider.activeElementObserver){
        throw new Error('<ChartTooltip> was rendered before ChartJSProvider was set');
    }
    useSubscription(() => chartJSProvider.activeElementObserver!);

    const parentRef = useRef<HTMLDivElement>(null);
    const { backgroundColor } = useInstance(ChartTooltipConfig);

    // wide enough to fix long date
    // fixed so we can calculate its center
    const width = 130;

    // used to have a common height between parent container (that pushed the chart down)
    // and the abosuletely positioned tooltip (which moves left to right to match the active element in the chart)
    const height = 65;

    // use a ref so we remember the position when mouse leaves chart on desktop
    const left = useRef(0);

    if (chartJSProvider.activeElementObserver!.state.active){
        const { x } = chartJSProvider.activeElementObserver!.state.active;
        let newLeft = x - (width / 2);

        if (newLeft < 0) {
            newLeft = 0;
        }

        if (parentRef.current) {
            const maxLeft = parentRef.current.clientWidth - width;
            if (maxLeft > 0 && newLeft > maxLeft) {
                newLeft = maxLeft;
            }
        }

        left.current = newLeft;
    }

    const active = vm.getAllDataForActiveHour();

    return (
        <div style={{ height, position: 'relative' }} ref={parentRef}>
            <div style={{
                width,
                height,
                backgroundColor: `var(${backgroundColor})`,
                position: 'absolute',
                left: left.current,
                color: 'var(--ion-color-medium)',
                borderRadius: 10,
                textAlign: 'center',
                padding: 5,
                marginLeft: 5,
                display: active ? 'block' : 'none'
            }}>
                {active && (
                    <>
                        <p style={{ margin: 0, padding: 0, fontSize: 13 }}>
                            {vm.formatTime(active.forecastStart, 'ha, ddd MMM D')}
                        </p>

                        <p style={{ margin: 0, padding: 0, display: 'flex', justifyContent: 'center'}}>
                            <WeatherConditionIcon size="medium" conditionCode={active.conditionCode} isDaylight={active.isDaylight} />
                            {/* TODO: icon and text should be vertically, middle aligned */}
                            <span style={{ fontSize: 20, color: 'var(--ion-color-light-contrast)', marginLeft: 5 }}>
                                {active.temperatureF}º
                                <span style={{ fontSize: 'x-small' }}>F</span>
                            </span>
                        </p>
                        <p style={{ fontSize: 13, margin: 0, padding: 0 }}>{getWeatherLabel(active.conditionCode)}</p>
                    </>
                )}
            </div>
        </div>
    );
};

const weatherCodeToLabel: { [key in IWeatherCondition]: string } = {
    Blizzard: 'Blizzard',
    BlowingDust: 'Blowing Dust',
    BlowingSnow: 'Blowing Snow',
    Breezy: 'Breezy',
    Clear: 'Clear',
    Cloudy: 'Cloudy',
    Drizzle: 'Drizzle',
    Flurries: 'Flurries',
    Foggy: 'Foggy',
    FreezingDrizzle: 'Freezing Drizzle',
    FreezingRain: 'Freezing Rain',
    Frigid: 'Frigid',
    Hail: 'Hail',
    Haze: 'Haze',
    HeavyRain: 'Heavy Rain',
    HeavySnow: 'Heavy Snow',
    Hot: 'Hot',
    Hurricane: 'Hurricane',
    IsolatedThunderstorm: 'Isolated Thunderstorms', // deprecated by apple
    IsolatedThunderstorms: 'Isolated Thunderstorms',
    MostlyClear: 'Mostly Clear',
    MostlyCloudy: 'Mostly Cloudy',
    PartlyClear: 'Partly Clear', // deprecated by apple
    PartlyCloudy: 'Partly Cloudy',
    Rain: 'Rain',

    // TODO: does this fit
    ScatteredThunderstorms: 'Scattered Thunderstorms',

    Sleet: 'Sleet',
    Smoky: 'Smoky',
    Snow: 'Snow',
    StrongStorms: 'Strong Storms',
    SunFlurries: 'Sun Flurries',
    SunShowers: 'Sun Showers',
    Thunderstorms: 'Thunderstorms',
    TropicalStorm: 'Tropical Storm',
    Windy: 'Windy',
    WintryMix: 'Wintry Mix'
};

const getWeatherLabel = (weatherCode: string): string => {
    // TODO: log to senty if code we didn't expect
    return (weatherCodeToLabel as any)[weatherCode] || weatherCode;
};
